import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { message } from 'antd'
import SettingsService from 'services/SettingsService'

export const initialState = {
	loading: true,
	loadingArticleGallery: false,
	settingsLoaded: false,
	generalSettingsLoaded: false,
	articleGalleryLoaded: false,
	homeSettingsData: {
		sectionHero: {
			template: null,
			title: null,
			subtitle: null,
		},
		sectionAbout: {
			template: null,
			bio: null,
			bioCd: null,
		},
		customSections: [],
	},
	generalSettingsData: {
		settings: {
			contactMail: null,
			socials: {},
		},
		isLanguageChangeEnabled: true,
	},
	articleGalleryTemplate: null,
}

// Homepage settings
export const getHomepageSettings = createAsyncThunk(
	'getSettings/homepage',
	async (lang, { rejectWithValue }) => {
		try {
			const response = await SettingsService.getHomepageSettings(lang)
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

export const setHomepageSettings = createAsyncThunk(
	'setSettings/homepage',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.setHomepageSettings(data)
			return response
		} catch (err) {
			// The validation middleware responds with { errors: [...] } and no `message`,
			// so surface the Joi messages instead of a generic 'Error'.
			const responseData = err.response?.data
			const joiErrors = Array.isArray(responseData?.errors) ? responseData.errors.join('; ') : null
			return rejectWithValue(responseData?.message || joiErrors || 'Error')
		}
	},
)

// General settings
export const getGeneralSettings = createAsyncThunk(
	'getSettings/general',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.getSettings({ name: 'general' })
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

export const setGeneralSettings = createAsyncThunk(
	'setSettings/general',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.setSettings({
				name: 'general',
				content: data,
			})
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

// Language change setting
export const getLanguageChangeSetting = createAsyncThunk(
	'getSettings/language-change',
	async (_, { rejectWithValue }) => {
		try {
			const response = await SettingsService.getLanguageChangeSetting()
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

export const setLanguageChangeSetting = createAsyncThunk(
	'setSettings/language-change',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.setLanguageChangeSetting(data)
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

// Article gallery template
export const getArticleTemplateSettings = createAsyncThunk(
	'getSettings/article-gallery',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.getSettings({ name: 'article-gallery-template' })
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

export const setArticleTemplateSettings = createAsyncThunk(
	'setSettings/article-gallery',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SettingsService.setSettings({
				name: 'article-gallery-template',
				content: data,
			})
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	},
)

export const settingsSlice = createSlice({
	name: 'settings',
	initialState,
	extraReducers: (builder) => {
		builder
			// Homepage | Get
			.addCase(getHomepageSettings.pending, (state) => {
				state.loading = true
				state.settingsLoaded = false
			})
			.addCase(getHomepageSettings.fulfilled, (state, action) => {
				state.homeSettingsData = action.payload
				state.loading = false
				state.settingsLoaded = true
			})
			.addCase(getHomepageSettings.rejected, (state, action) => {
				state.loading = false
				message.error(action.payload || 'Failed to load homepage settings.')
			})

			// Homepage | Set
			.addCase(setHomepageSettings.pending, (state) => {
				state.loading = true
			})
			.addCase(setHomepageSettings.fulfilled, (state, action) => {
				state.loading = false
				message.success(action.payload.message)
			})
			.addCase(setHomepageSettings.rejected, (state, action) => {
				state.loading = false
				message.error(action.payload)
			})

			// General | Get
			.addCase(getGeneralSettings.pending, (state) => {
				state.loading = true
				state.generalSettingsLoaded = false
			})
			.addCase(getGeneralSettings.fulfilled, (state, action) => {
				state.generalSettingsData = action.payload
				state.loading = false
				state.generalSettingsLoaded = true
			})
			.addCase(getGeneralSettings.rejected, (state) => {
				state.loading = false
			})

			// General | Set
			.addCase(setGeneralSettings.pending, (state) => {
				state.loading = true
			})
			.addCase(setGeneralSettings.fulfilled, (state, action) => {
				state.loading = false
				message.success(action.payload.message)
			})
			.addCase(setGeneralSettings.rejected, (state, action) => {
				state.loading = false
				message.error(action.payload)
			})

			// Language Change Setting | Get
			.addCase(getLanguageChangeSetting.pending, (state) => {
				state.loading = true
			})
			.addCase(getLanguageChangeSetting.fulfilled, (state, action) => {
				state.generalSettingsData.isLanguageChangeEnabled = action.payload.isLanguageChangeEnabled
				state.loading = false
			})
			.addCase(getLanguageChangeSetting.rejected, (state) => {
				state.loading = false
			})

			// Language Change Setting | Set
			.addCase(setLanguageChangeSetting.pending, (state) => {
				state.loading = true
			})
			.addCase(setLanguageChangeSetting.fulfilled, (state, action) => {
				state.loading = false
				message.success(action.payload.message)
			})
			.addCase(setLanguageChangeSetting.rejected, (state, action) => {
				state.loading = false
				message.error(action.payload)
			})

			// Article gallery template | Get
			.addCase(getArticleTemplateSettings.pending, (state) => {
				state.loadingArticleGallery = true
				state.articleGalleryLoaded = false
			})
			.addCase(getArticleTemplateSettings.fulfilled, (state, action) => {
				state.articleGalleryTemplate = action.payload
				state.articleGalleryLoaded = true
				state.loadingArticleGallery = false
			})
			.addCase(getArticleTemplateSettings.rejected, (state) => {
				state.loadingArticleGallery = false
			})

			// Article gallery template | Set
			.addCase(setArticleTemplateSettings.pending, (state) => {
				state.loadingArticleGallery = true
			})
			.addCase(setArticleTemplateSettings.fulfilled, (state, action) => {
				state.loadingArticleGallery = false
				message.success(action.payload.message)
			})
			.addCase(setArticleTemplateSettings.rejected, (state, action) => {
				state.loadingArticleGallery = false
				message.error(action.payload)
			})
	},
})

export default settingsSlice.reducer
